import type { PaletteModel, PaletteClassName, PaletteClass } from '../data/types';
import type { SurfaceKind } from '../world/mesh/attributes';
import { SURFACE } from '../world/mesh/attributes';
import { luminance } from '../core/color';

/**
 * Maps SURFACE enum values to palette class names.
 * From PLAN.md section 4 (painted material).
 */
export const SURFACE_TO_CLASS: Readonly<Record<SurfaceKind, PaletteClassName>> = {
  [SURFACE.ground]: 'nearFields',
  [SURFACE.field]: 'polderFields',
  [SURFACE.garden]: 'gardenGrass',
  [SURFACE.street]: 'streetGround',
  [SURFACE.plaster]: 'wallLight',
  [SURFACE.brick]: 'wallBrick',
  [SURFACE.stone]: 'towerStone',
  [SURFACE.wood]: 'shipHull',
  [SURFACE.roofTile]: 'roofTile',
  [SURFACE.roofSlate]: 'roofSlate',
  [SURFACE.roofThatch]: 'roofDark',
  [SURFACE.foliage]: 'treeFoliage',
  [SURFACE.sail]: 'sail',
  [SURFACE.cloth]: 'flagRed',
  [SURFACE.linework]: 'lineworkDark',
  [SURFACE.quayEarth]: 'dikeRoads',
  [SURFACE.smoke]: 'gunSmoke',
};

export interface ShadeRatios {
  readonly low: number;
  readonly high: number;
}

/**
 * Computes shade ratios from palette class luminance percentiles.
 * low = p10/median luminance, high = p90/median luminance.
 * These scale the lighting so shadow and lit surfaces match the painting's measured spread.
 */
export function computeShadeRatios(cls: PaletteClass): ShadeRatios {
  const medianLum = luminance(cls.median.linear);
  const p10Lum = luminance(cls.p10.linear);
  const p90Lum = luminance(cls.p90.linear);

  if (medianLum < 1e-6) {
    return { low: 1, high: 1 };
  }

  return {
    low: p10Lum / medianLum,
    high: p90Lum / medianLum,
  };
}

/**
 * TypeScript mirror of applyShading in painted.frag: wrapped Lambert 0, 0.5 and 1 give the class's
 * p10, median and p90 luminance ratios, linear between.
 */
export function surfaceShade(ratios: ShadeRatios, wrappedLambert: number): number {
  const t = Math.min(Math.max(wrappedLambert, 0), 1);
  const shade =
    t < 0.5 ? ratios.low + (1 - ratios.low) * t * 2 : 1 + (ratios.high - 1) * (t - 0.5) * 2;
  return Math.min(Math.max(shade, ratios.low), ratios.high);
}

/**
 * House walls at walking height (STYLE §12, round 18). The painter keeps his walls near one tone in
 * every light: the museum scan's house walls read 43.6/0.5/34.6 (Warmoesstraat), 47.5/1.6/33.7
 * (Nieuwendijk) and 42.2/2.6/34.4 (Dam), their lower quartile 34-39/0/28-29 (houses, round 18).
 * With every wall at the class median (47.3/1.1/32.8) the class's p10-p90 range took street shade
 * to 38.5/1.0/28.1 and the sunlit Dam fronts to 58.9/1.3/39.0. The walls take the range to the power
 * rangeExponent, and in shade keep their chroma: shadeColour scales the colour's difference from its
 * luminance by the shade to the power shadeChromaExponent. Fitted at walking height in the
 * Warmoesstraat, the Nieuwendijk and on the Dam with the houses' own albedos, holding the painter
 * view: a narrower range (0.5, 0.6) moved its histogram distance from 0.1151 to 0.1171 and 0.1168
 * (STYLE §12).
 */
export const WALL_LIGHT = {
  surfaces: [SURFACE.plaster, SURFACE.brick],
  rangeExponent: 0.7,
  shadeChromaExponent: 0.5,
} as const;

function isWall(kind: SurfaceKind): boolean {
  return (WALL_LIGHT.surfaces as readonly SurfaceKind[]).includes(kind);
}

/**
 * TypeScript mirror of painted.frag shadeColour: the albedo lit by the shade, its luminance scaled
 * by the shade and its difference from that luminance by the shade to the power chromaExponent
 * where the shade darkens it.
 */
export function shadeColour(
  albedo: readonly [number, number, number],
  shade: number,
  chromaExponent: number
): [number, number, number] {
  const lum = luminance(albedo);
  const chroma = shade < 1 ? shade ** chromaExponent : shade;
  return [0, 1, 2].map((i) => lum * shade + (albedo[i]! - lum) * chroma) as [
    number,
    number,
    number,
  ];
}

export type SurfaceShadeRatios = Readonly<Record<SurfaceKind, ShadeRatios>>;

/**
 * Builds shade ratios for all surface types from the palette.
 */
export function buildSurfaceShadeRatios(palette: PaletteModel): SurfaceShadeRatios {
  const result = {} as Record<SurfaceKind, ShadeRatios>;

  for (const [surfaceKey, className] of Object.entries(SURFACE_TO_CLASS)) {
    const surfaceKind = Number(surfaceKey) as SurfaceKind;
    const ratios = computeShadeRatios(palette[className]);
    const k = isWall(surfaceKind) ? WALL_LIGHT.rangeExponent : 1;
    result[surfaceKind] = { low: ratios.low ** k, high: ratios.high ** k };
  }

  return result;
}

/*
 * SURFACE codes run from 0 without gaps (surfaceShading.test.ts checks), so the count sizes arrays
 * that the shaders index by code. A hard-coded size let a new code (smoke, 16) read past the end.
 */
export const SURFACE_COUNT = Object.keys(SURFACE).length;

/**
 * Creates the shader's uShadeLow and uShadeHigh arrays, one entry per SURFACE code.
 * The middle band maps to exactly 1.0 (median stays median).
 */
export function createShadeUniformArrays(ratios: SurfaceShadeRatios): {
  readonly shadeLow: Float32Array;
  readonly shadeHigh: Float32Array;
  readonly shadeChroma: Float32Array;
} {
  const kinds = Array.from({ length: SURFACE_COUNT }, (_, i) => i as SurfaceKind);
  return {
    shadeLow: Float32Array.from(kinds, (kind) => ratios[kind].low),
    shadeHigh: Float32Array.from(kinds, (kind) => ratios[kind].high),
    shadeChroma: Float32Array.from(kinds, (kind) =>
      isWall(kind) ? WALL_LIGHT.shadeChromaExponent : 1
    ),
  };
}
